import apiClient from './apiClient';
import { mockTimeEntries } from './mockData';

// Flag to use mock data for development
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

/**
 * Helper function to handle errors
 * @param {object} error - Error object
 * @returns {Error} - Formatted error
 */
const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  if (message) {
    return new Error(message);
  }
  return error;
};

// Time Entry Service Functions
const timeEntryService = {
  // Calendar day-entry flow (Work/Break start-stop) — Phase 2.
  getCalendarMonth: async (year, month) => {
    const response = await apiClient.get('/employee/calendar-month', { params: { year, month } });
    return response.data.data;
  },
  getDay: async (date, lang) => {
    const response = await apiClient.get('/employee/day', { params: { date, lang } });
    return response.data.data;
  },
  // Contest resolution (REQ #13): { date, agree:boolean, comment? }
  resolveDay: async (payload) => {
    try {
      const response = await apiClient.post('/employee/day/resolve', payload);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },
  // Employee dashboard summary (REQ #12): missing / contested / awaiting.
  getEmployeeDashboard: async () => {
    const response = await apiClient.get('/employee/dashboard');
    return response.data.data;
  },
  // payload: { date, dayOff?, work:[{start,end}], breaks:[{start,end}] }
  saveDay: async (payload) => {
    try {
      const response = await apiClient.post('/employee/day', payload);
      return response.data.data;
    } catch (error) {
      // Surface the context-specific validation messages (error.response.data.error.details)
      const err = error.response && error.response.data && error.response.data.error;
      const e = new Error((err && err.message) || error.message);
      e.details = (err && err.details) || null;
      throw e;
    }
  },

  /**
   * Get time entries for the current user (employee)
   * @param {object} params - Filter parameters
   * @returns {Promise} - Promise containing time entries
   */
  getEmployeeTimeEntries: async (params = {}) => {
    if (USE_MOCK_DATA) {
      // Use mock data for development
      return new Promise((resolve) => {
        // Simple filtering based on params
        let filteredEntries = [...mockTimeEntries];
        
        if (params.customerId) {
          filteredEntries = filteredEntries.filter(entry => 
            entry.customerId == params.customerId);
        }
        
        if (params.status) {
          filteredEntries = filteredEntries.filter(entry => 
            entry.status === params.status);
        }
        
        if (params.dateFrom || params.startDate) {
          const startDate = params.startDate || params.dateFrom;
          filteredEntries = filteredEntries.filter(entry => 
            new Date(entry.date) >= new Date(startDate));
        }
        
        if (params.dateTo || params.endDate) {
          const endDate = params.endDate || params.dateTo;
          filteredEntries = filteredEntries.filter(entry => 
            new Date(entry.date) <= new Date(endDate));
        }
        
        // Simulate network delay
        setTimeout(() => resolve(filteredEntries), 500);
      });
    }
    
    try {
      const response = await apiClient.get('/employee/time-entries', { params });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Get one week's grid for the employee (a customer+project selection).
   * @param {object} params - { weekStart?, customerId?, projectId? }
   * @returns {Promise} week payload { weekStart, weekEnd, customerId, projectId,
   *   customerName, projectName, days: [{date, dayIndex, durationMinutes,
   *   durationFormatted, status, entryId, editable}], weekTotalMinutes }
   */
  getWeek: async (params = {}) => {
    try {
      const response = await apiClient.get('/employee/week', { params });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Batch-save a week for one customer+project.
   * @param {object} payload - { customerId, projectId, weekStart,
   *   days: [{date, durationMinutes}] }
   * @returns {Promise} updated week payload
   */
  saveWeek: async (payload) => {
    try {
      const response = await apiClient.post('/employee/week', payload);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Mark a week as saved/submitted (turns the save-week button green) without
   * mutating individual day entries — used by the calendar where days are
   * already persisted inline.
   * @param {string} weekStart - any 'YYYY-MM-DD' in the target week
   * @returns {Promise} { weekStart, submitted, submittedAt }
   */
  submitWeek: async (weekStart) => {
    try {
      const response = await apiClient.post('/employee/week/submit', { weekStart });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * List the week-start dates the employee has already submitted within a range
   * (inclusive), so the calendar can colour each week's save button.
   * @param {string} from - 'YYYY-MM-DD'
   * @param {string} to - 'YYYY-MM-DD'
   * @returns {Promise<string[]>} submitted week-start dates ('YYYY-MM-DD')
   */
  getWeekSubmissions: async (from, to) => {
    try {
      const response = await apiClient.get('/employee/week-submissions', { params: { from, to } });
      const data = response.data.data;
      return Array.isArray(data) ? data : (data && data.weekStarts) || [];
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Get the employee's persisted weekly-grid selection.
   * @returns {Promise} { defaultCustomerId, defaultProjectId }
   */
  getPreferences: async () => {
    try {
      const response = await apiClient.get('/employee/preferences');
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Persist the employee's weekly-grid selection.
   * @param {object} prefs - { defaultCustomerId, defaultProjectId }
   * @returns {Promise} saved preferences
   */
  updatePreferences: async (prefs) => {
    try {
      const response = await apiClient.put('/employee/preferences', prefs);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Get a specific time entry by ID (employee)
   * @param {string} id - Time entry ID
   * @returns {Promise} - Promise containing time entry
   */
  getEmployeeTimeEntryById: async (id) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve, reject) => {
        const entry = mockTimeEntries.find(entry => entry.id == id);
        
        if (entry) {
          // Simulate network delay
          setTimeout(() => resolve(entry), 500);
        } else {
          setTimeout(() => reject(new Error('Time entry not found')), 500);
        }
      });
    }
    
    try {
      const response = await apiClient.get(`/employee/time-entries/${id}`);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Create a new time entry (employee)
   * @param {object} timeEntryData - Time entry data
   * @returns {Promise} - Promise containing the created time entry
   */
  createTimeEntry: async (timeEntryData) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        // Generate a new ID for the entry
        const newId = mockTimeEntries.length > 0 
          ? Math.max(...mockTimeEntries.map(entry => entry.id)) + 1 
          : 1;
        
        // Create a new entry with the provided data
        const newEntry = {
          ...timeEntryData,
          id: newId,
          employeeId: 101, // Mock employee ID
          status: 'pending',
          // Add additional derived fields for display purposes
          customerName: mockTimeEntries.find(
            entry => entry.customerId == timeEntryData.customerId
          )?.customerName || 'Unknown Customer',
          projectName: 'Project ' + timeEntryData.projectId,
        };
        
        // Add to mock data
        mockTimeEntries.unshift(newEntry);
        
        // Simulate network delay
        setTimeout(() => resolve(newEntry), 500);
      });
    }
    
    try {
      const response = await apiClient.post('/employee/time-entries', timeEntryData);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Update an existing time entry (employee)
   * @param {string} id - Time entry ID
   * @param {object} timeEntryData - Updated time entry data
   * @returns {Promise} - Promise containing the updated time entry
   */
  updateTimeEntry: async (id, timeEntryData) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve, reject) => {
        const index = mockTimeEntries.findIndex(entry => entry.id == id);
        
        if (index !== -1) {
          // Update the time entry in the mock data
          mockTimeEntries[index] = {
            ...mockTimeEntries[index],
            ...timeEntryData,
            id: parseInt(id) // Make sure ID remains the same
          };
          
          // Simulate network delay
          setTimeout(() => resolve(mockTimeEntries[index]), 500);
        } else {
          setTimeout(() => reject(new Error('Time entry not found')), 500);
        }
      });
    }
    
    try {
      const response = await apiClient.put(`/employee/time-entries/${id}`, timeEntryData);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Delete a time entry (employee)
   * @param {string} id - Time entry ID
   * @returns {Promise} - Promise containing success message
   */
  deleteTimeEntry: async (id) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve, reject) => {
        const index = mockTimeEntries.findIndex(entry => entry.id == id);
        
        if (index !== -1) {
          // Remove the time entry from the mock data
          mockTimeEntries.splice(index, 1);
          
          // Simulate network delay
          setTimeout(() => resolve({ message: 'Time entry deleted successfully' }), 500);
        } else {
          setTimeout(() => reject(new Error('Time entry not found')), 500);
        }
      });
    }
    
    try {
      const response = await apiClient.delete(`/employee/time-entries/${id}`);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Get employee time statistics
   * @returns {Promise} - Promise containing time statistics
   */
  getEmployeeTimeStatistics: async () => {
    try {
      const response = await apiClient.get('/employee/statistics');
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Get time entries pending approval (customer)
   * @param {object} params - Filter parameters
   * @returns {Promise} - Promise containing time entries pending approval
   */
  getPendingApprovals: async (params = {}) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        // Filter entries based on status and params
        let filteredEntries = [...mockTimeEntries].filter(
          entry => entry.status === 'pending' || params.showAll
        );
        
        // Additional filtering based on params
        if (params.employeeId) {
          filteredEntries = filteredEntries.filter(entry => 
            entry.employeeId == params.employeeId);
        }
        
        if (params.dateFrom || params.startDate) {
          const startDate = params.startDate || params.dateFrom;
          filteredEntries = filteredEntries.filter(entry => 
            new Date(entry.date) >= new Date(startDate));
        }
        
        if (params.dateTo || params.endDate) {
          const endDate = params.endDate || params.dateTo;
          filteredEntries = filteredEntries.filter(entry => 
            new Date(entry.date) <= new Date(endDate));
        }
        
        // Simulate network delay
        setTimeout(() => resolve(filteredEntries), 500);
      });
    }
    
    try {
      const response = await apiClient.get('/customer/approvals', { params });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Get a specific time entry for approval (customer)
   * @param {string} id - Time entry ID
   * @returns {Promise} - Promise containing time entry
   */
  getApprovalById: async (id) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve, reject) => {
        const entry = mockTimeEntries.find(entry => entry.id == id);
        
        if (entry) {
          // Simulate network delay
          setTimeout(() => resolve(entry), 500);
        } else {
          setTimeout(() => reject(new Error('Time entry not found')), 500);
        }
      });
    }
    
    try {
      const response = await apiClient.get(`/customer/approvals/${id}`);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Approve a time entry (customer)
   * @param {string} id - Time entry ID
   * @returns {Promise} - Promise containing success message
   */
  approveTimeEntry: async (id) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve, reject) => {
        const index = mockTimeEntries.findIndex(entry => entry.id == id);
        
        if (index !== -1) {
          // Update the time entry status
          mockTimeEntries[index].status = 'approved';
          
          // Add to history
          const now = new Date().toISOString();
          
          if (!mockTimeEntries[index].history) {
            mockTimeEntries[index].history = [];
          }
          
          mockTimeEntries[index].history.push({
            timestamp: now,
            action: 'Approved by Customer Admin'
          });
          
          // Simulate network delay
          setTimeout(() => resolve({ message: 'Time entry approved successfully' }), 500);
        } else {
          setTimeout(() => reject(new Error('Time entry not found')), 500);
        }
      });
    }
    
    try {
      const response = await apiClient.post(`/customer/approvals/${id}/approve`);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Challenge a time entry (customer)
   * @param {string} id - Time entry ID
   * @param {string} reason - Reason for challenging
   * @returns {Promise} - Promise containing success message
   */
  challengeTimeEntry: async (id, reason, hours) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve, reject) => {
        const index = mockTimeEntries.findIndex(entry => entry.id == id);

        if (index !== -1) {
          // Update the time entry status
          mockTimeEntries[index].status = 'challenged';
          mockTimeEntries[index].challengeReason = reason;
          
          // Add to history
          const now = new Date().toISOString();
          
          if (!mockTimeEntries[index].history) {
            mockTimeEntries[index].history = [];
          }
          
          mockTimeEntries[index].history.push({
            timestamp: now,
            action: 'Challenged by Customer Admin',
            note: reason
          });
          
          // Simulate network delay
          setTimeout(() => resolve({ message: 'Time entry challenged successfully' }), 500);
        } else {
          setTimeout(() => reject(new Error('Time entry not found')), 500);
        }
      });
    }
    
    try {
      // hours = the hours the customer DOES approve (0–24); omitted → not set (REQ #18).
      const body = { reason };
      if (hours !== undefined && hours !== null && hours !== '') body.hours = hours;
      const response = await apiClient.post(`/customer/approvals/${id}/challenge`, body);
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Get time entries for the customer (paginated)
   * @param {object} params - Filter parameters (employeeId, startDate, endDate, status, page, limit)
   * @returns {Promise} - Promise containing {items, total, page, totalPages}
   */
  getCustomerTimeEntries: async (params = {}) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        let filteredEntries = [...mockTimeEntries];

        if (params.employeeId) {
          filteredEntries = filteredEntries.filter(entry =>
            entry.employeeId == params.employeeId);
        }

        if (params.status) {
          filteredEntries = filteredEntries.filter(entry =>
            entry.status === params.status);
        }

        if (params.startDate) {
          filteredEntries = filteredEntries.filter(entry =>
            new Date(entry.date) >= new Date(params.startDate));
        }

        if (params.endDate) {
          filteredEntries = filteredEntries.filter(entry =>
            new Date(entry.date) <= new Date(params.endDate));
        }

        // Pagination
        const page = parseInt(params.page, 10) || 1;
        const limit = parseInt(params.limit, 10) || 31;
        const startIndex = (page - 1) * limit;
        const paginatedEntries = filteredEntries.slice(startIndex, startIndex + limit);

        setTimeout(() => resolve({
          items: paginatedEntries,
          total: filteredEntries.length,
          page,
          totalPages: Math.ceil(filteredEntries.length / limit)
        }), 500);
      });
    }

    try {
      const response = await apiClient.get('/customer/time-entries', { params });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Get monthly time entries summary for the customer
   * @param {object} params - Filter parameters ({year, month})
   * @returns {Promise} - Promise containing {year, month, totalSummary, employeeSummary}
   */
  getCustomerTimeEntriesSummary: async (params = {}) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        const now = new Date();
        const year = parseInt(params.year, 10) || now.getFullYear();
        const month = parseInt(params.month, 10) || now.getMonth() + 1;

        const monthEntries = mockTimeEntries.filter(entry => {
          const entryDate = new Date(entry.date);
          return entryDate.getFullYear() === year && entryDate.getMonth() + 1 === month;
        });

        const sumHours = (entries) => entries.reduce((sum, entry) => sum + Number(entry.hours), 0);

        const buildSummary = (entries) => ({
          totalHours: sumHours(entries),
          approvedHours: sumHours(entries.filter(e => e.status === 'approved')),
          pendingHours: sumHours(entries.filter(e => e.status === 'pending')),
          challengedHours: sumHours(entries.filter(e => e.status === 'challenged')),
          entryCount: entries.length
        });

        const employeeIds = [...new Set(monthEntries.map(entry => entry.employeeId))];
        const employeeSummary = employeeIds.map(employeeId => {
          const employeeEntries = monthEntries.filter(entry => entry.employeeId === employeeId);
          return {
            employeeId,
            employeeName: employeeEntries[0]?.employeeName || 'Unknown Employee',
            ...buildSummary(employeeEntries)
          };
        });

        setTimeout(() => resolve({
          year,
          month,
          totalSummary: buildSummary(monthEntries),
          employeeSummary
        }), 500);
      });
    }

    try {
      const response = await apiClient.get('/customer/time-entries/summary', { params });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Approve all pending time entries of an employee for a month (customer)
   * @param {number|string} employeeId - Employee ID (employees.id)
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @returns {Promise} - Promise containing {message, count}
   */
  approveMonth: async (employeeId, year, month) => {
    if (USE_MOCK_DATA) {
      return new Promise((resolve) => {
        let count = 0;

        mockTimeEntries.forEach(entry => {
          const entryDate = new Date(entry.date);
          if (
            entry.employeeId == employeeId &&
            entry.status === 'pending' &&
            entryDate.getFullYear() === parseInt(year, 10) &&
            entryDate.getMonth() + 1 === parseInt(month, 10)
          ) {
            entry.status = 'approved';

            if (!entry.history) {
              entry.history = [];
            }

            entry.history.push({
              timestamp: new Date().toISOString(),
              action: 'Approved by Customer Admin'
            });

            count += 1;
          }
        });

        setTimeout(() => resolve({
          message: `${count} time entries approved successfully`,
          count
        }), 500);
      });
    }

    try {
      const response = await apiClient.post('/customer/approvals/approve-month', {
        employeeId,
        year,
        month
      });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Approve all pending entries of an employee for a given week (customer).
   * @param {number} employeeId
   * @param {string} weekStart - any date in the week (YYYY-MM-DD)
   * @returns {Promise} { message, count }
   */
  approveWeek: async (employeeId, weekStart) => {
    try {
      const response = await apiClient.post('/customer/approvals/approve-week', { employeeId, weekStart });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Approve a specific set of time entries (customer).
   * @param {number[]} entryIds
   * @returns {Promise} { message, count }
   */
  bulkApprove: async (entryIds) => {
    try {
      const response = await apiClient.post('/customer/approvals/bulk-approve', { entryIds });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Challenge a specific set of time entries with a reason (customer).
   * @param {number[]} entryIds
   * @param {string} reason
   * @returns {Promise} { message, count }
   */
  bulkChallenge: async (entryIds, reason) => {
    try {
      const response = await apiClient.post('/customer/approvals/bulk-challenge', { entryIds, reason });
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  },

  /**
   * Get customer time statistics
   * @returns {Promise} - Promise containing time statistics
   */
  getCustomerTimeStatistics: async () => {
    try {
      const response = await apiClient.get('/customer/statistics');
      return response.data.data;
    } catch (error) {
      throw handleError(error);
    }
  }
};

export { timeEntryService };